import {
	getLiteraDisabledHoverText,
	getInlineDisabledHoverText,
} from "../../utilities/utilities";

export function buildMajorVersionRows(documentList, labels, majorVersionsData) {
	const mdvData = (labels ?? []).map((l) => ({
		num: l.major,
		revNum: l.saveNumber,
	}));
	const taggedRevNums = new Set(mdvData.map((e) => e.revNum));
	const activeMdvNums = new Set(mdvData.map((e) => e.num));
	const activeRevNums = new Set(documentList.map((v) => v.versionNumber));

	const majorVersionsMap = {};
	(majorVersionsData || []).forEach((mv) => {
		majorVersionsMap[mv.majorVersionNum] = mv;
	});

	const saveByVersionNumber = {};
	documentList.forEach((save) => {
		saveByVersionNumber[save.versionNumber] = save;
	});

	const rows = [];
	const uniqueNums = [...activeMdvNums].sort((a, b) => a - b);

	for (const num of uniqueNums) {
		const entries = mdvData.filter((e) => e.num === num);
		const activeEntries = entries.filter((e) =>
			activeRevNums.has(e.revNum)
		);
		const implicitSaves = documentList.filter(
			(v) =>
				v.versionNumber === num && !taggedRevNums.has(v.versionNumber)
		);

		const allRevNums = [
			...entries.map((e) => e.revNum),
			...implicitSaves.map((v) => v.versionNumber),
		];
		const minRevNum = allRevNums.length > 0 ? Math.min(...allRevNums) : num;
		const mv = majorVersionsMap[num];
		const saveCount = activeEntries.length + implicitSaves.length;
		if (saveCount === 0) continue;

		const subRowSaves = [
			...activeEntries
				.map((e) => saveByVersionNumber[e.revNum])
				.filter(Boolean),
			...implicitSaves,
		];

		rows.push({
			id: `mdv-${num}`,
			ver: `v${num}`,
			verNum: num,
			saveCount,
			sortKey: minRevNum,
			isAutoVersioned: false,
			createdAt: mv?.createdAt,
			createdBy: mv?.createdBy,
			comment: mv?.comment,
			subRows: subRowSaves.map((save) => ({
				...save,
				id: `save-${save.versionNumber}`,
				verNum: save.versionNumber,
				isSubRow: true,
				parentSaveCount: subRowSaves.length,
				subRows: [],
			})),
		});
	}

	for (const save of documentList) {
		if (
			!taggedRevNums.has(save.versionNumber) &&
			!activeMdvNums.has(save.versionNumber)
		) {
			rows.push({
				id: `autoVersioned-${save.versionNumber}`,
				ver: `v${save.versionNumber}`,
				verNum: save.versionNumber,
				versionID: save.versionID,
				saveCount: 1,
				sortKey: save.versionNumber,
				isAutoVersioned: true,
				createdAt: save.createdAt,
				createdBy: save.createdBy,
				comment: save.comment,
				subRows: [
					{
						...save,
						id: `save-${save.versionNumber}`,
						verNum: save.versionNumber,
						isSubRow: true,
						parentSaveCount: 1,
						subRows: [],
					},
				],
			});
		}
	}

	rows.sort((a, b) => a.sortKey - b.sortKey);
	return rows;
}

export function buildAutoRows(documentList) {
	return documentList.map((save) => ({
		id: `auto-${save.versionNumber}`,
		verNum: save.versionNumber,
		versionID: save.versionID,
		ver: `v${save.versionNumber}`,
		createdAt: save.createdAt,
		createdBy: save.createdBy,
		comment: save.comment,
		saveCount: 1,
	}));
}

export function computeCompareDisabledTexts({
	versionID,
	isLatest,
	documentID,
	firstDocIdInDocumentCompare,
	firstVersionIdInDocumentCompare,
	docCompareType,
	isValidLiteraFileType,
	isValidInlineFileType,
	isLiteraCompareVisible,
	isLiteraCompareEnabled,
	isInlineCompareEnabled,
	launcherInstalled,
	documentLifecycleStatusObject,
}) {
	const isAlreadySelected =
		firstDocIdInDocumentCompare === documentID &&
		(firstVersionIdInDocumentCompare === versionID ||
			(!firstVersionIdInDocumentCompare && isLatest));
	const canAddToLitera = !docCompareType || docCompareType === "litera";
	const canAddToInline = !docCompareType || docCompareType === "inline";

	const literaDisabledText = isValidLiteraFileType
		? getLiteraDisabledHoverText({
				isLiteraCompareVisible,
				isLiteraCompareEnabled,
				isAlreadySelectedForDocCompare: isAlreadySelected,
				canAddToLiteraCompare: canAddToLitera,
				launcherInstalled,
				documentActionsAreDisabled:
					documentLifecycleStatusObject.disabled,
				documentActionsAreDisabledHoverText:
					documentLifecycleStatusObject.disabledText,
			})
		: null;

	const inlineDisabledText = isValidInlineFileType
		? getInlineDisabledHoverText({
				isInlineCompareEnabled,
				isAlreadySelectedForDocCompare: isAlreadySelected,
				canAddToInlineCompare: canAddToInline,
				documentActionsAreDisabled:
					documentLifecycleStatusObject.disabled,
				documentActionsAreDisabledHoverText:
					documentLifecycleStatusObject.disabledText,
			})
		: null;

	return { literaDisabledText, inlineDisabledText };
}

export function computeDeleteMajorVersionDisabled(
	row,
	{ deleteDisabledInfo, majorVersionCount, maxMajorVerNum }
) {
	if (deleteDisabledInfo.disabled) {
		return {
			disabled: true,
			disabledHoverText: deleteDisabledInfo.disabledHoverText,
		};
	}
	if (majorVersionCount === 1) {
		return {
			disabled: true,
			disabledHoverText: "Cannot delete the only Version",
		};
	}
	if (row.verNum === maxMajorVerNum) {
		return {
			disabled: true,
			disabledHoverText: "Cannot delete the current Version",
		};
	}
	return { disabled: false, disabledHoverText: "" };
}

export function computeDeleteSaveDisabled(save, { deleteDisabledInfo }) {
	if (save.parentSaveCount === 1) {
		return {
			disabled: true,
			disabledHoverText: "Cannot delete the last Save under a Version",
		};
	}
	if (deleteDisabledInfo.disabled) {
		return {
			disabled: true,
			disabledHoverText: deleteDisabledInfo.disabledHoverText,
		};
	}
	return { disabled: false, disabledHoverText: "" };
}

export function buildCommentDialogArgs(label, currentComment, isMajorVersion) {
	return {
		label,
		value: currentComment,
		canSubmit: true,
		submitButtonLabel: "Submit",
		useTextarea: true,
		maxLength: 1000,
		placeHolder: isMajorVersion
			? "Enter a Version Comment"
			: "Enter a Comment",
	};
}

// menuLabel: text shown in the dropdown e.g. "v1 Version Comment" or "v1.2 Comment"
// dialogLabel: used in the dialog title e.g. "v1" or "v1.2"
export function buildVersionCommentMenuItem({
	menuLabel,
	dialogLabel,
	currentComment,
	disabled,
	isMajorVersion,
	openGlobalDialog,
	disabledHoverText,
	onSubmit,
}) {
	const item = {
		text: (currentComment ? "Edit" : "Add") + ` ${menuLabel}`,
		disabledHoverText,
		onSelect: () =>
			openGlobalDialog(
				buildCommentDialogArgs(
					`Comment for ${dialogLabel}`,
					currentComment,
					isMajorVersion
				),
				(state) => onSubmit(state.value),
				() => ({ canSubmit: true, errorMessage: "" })
			),
	};
	if (disabled !== undefined) item.disabled = disabled;
	return item;
}

export function buildPreviewMenuItem({
	versionNumber,
	versionID,
	versionLabel,
	onPreview,
}) {
	return {
		text: "Preview",
		onSelect: () =>
			onPreview({
				versionNumber,
				versionID,
				...(versionLabel !== undefined ? { versionLabel } : {}),
			}),
	};
}

export function buildCompareMenuItems({
	versionID,
	documentID,
	documentDetails,
	addDocumentToCompare,
	isValidLiteraFileType,
	isValidInlineFileType,
	documentLifecycleStatusObject,
	literaDisabledText,
	inlineDisabledText,
}) {
	return [
		...(isValidLiteraFileType
			? [
					{
						text: "Compare in Litera",
						disabled:
							!!literaDisabledText ||
							documentLifecycleStatusObject.disabled,
						disabledHoverText: literaDisabledText,
						onSelect: () =>
							addDocumentToCompare({
								docID: documentID,
								filename: documentDetails.filename,
								versionID,
								type: "litera",
							}),
					},
				]
			: []),
		...(isValidInlineFileType
			? [
					{
						text: "Compare in LexWorkplace",
						disabled:
							!!inlineDisabledText ||
							documentLifecycleStatusObject.disabled,
						disabledHoverText: inlineDisabledText,
						onSelect: () =>
							addDocumentToCompare({
								docID: documentID,
								filename: documentDetails.filename,
								versionID,
								type: "inline",
							}),
					},
				]
			: []),
	];
}
